import * as React from "react";
import { useCallback, useState, useMemo } from "react";
import differenceWith from "lodash/differenceWith";
import intersectionWith from "lodash/intersectionWith";
import unionWith from "lodash/unionWith";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";
import { Link, IconButton } from "components/common";
import { widgetId, compareWidgetRefs } from "report/report-creation/content/types";
import Icon from "components/common/Icon";
import WidgetRow from "report/report-creation/content/WidgetRow";
import { StyledCheckbox, Row, SmallColumn, Column, ExpandButton } from "report/report-creation/content/common";
import Timestamp from "components/common/Timestamp";
import Routes from "routing/Routes";
const DashboardIcon = () => /* @__PURE__ */ React.createElement(Icon, { name: "dashboard", type: "regular" });
const DashboardRow = ({ dashboard, checked, widgetsInReport, onChange }) => {
  const { t } = useTranslation("enterprise_report");
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setExpanded((_expanded) => !_expanded), []);
  const _onCheck = useCallback((id) => onChange(unionWith(checked, [id], isEqual)), [checked, onChange]);
  const _onUncheck = useCallback(
    (id) => onChange(differenceWith(checked, [id], isEqual)),
    [checked, onChange]
  );
  const dashboardWidgetRefs = useMemo(
    () => dashboard.widgets.map((widget) => widgetId(widget, dashboard)),
    [dashboard]
  );
  const isWidgetInReport = useCallback(
    (widget) => widgetsInReport.find((w) => w.widgetId === widget.id && w.dashboardId === dashboard.id) !== void 0,
    [dashboard.id, widgetsInReport]
  );
  const addableWidgetRefs = useMemo(
    () => dashboard.widgets.filter((widget) => widget.eligible).filter((widget) => !isWidgetInReport(widget)).map((widget) => widgetId(widget, dashboard)),
    [dashboard, isWidgetInReport]
  );
  const _onChangeDashboard = useCallback(
    (e) => e.target.checked ? onChange(unionWith(checked, addableWidgetRefs, isEqual)) : onChange(differenceWith(checked, dashboardWidgetRefs, isEqual)),
    [addableWidgetRefs, checked, dashboardWidgetRefs, onChange]
  );
  const widgetRows = expanded ? dashboard.widgets.map((widget) => /* @__PURE__ */ React.createElement(
    WidgetRow,
    {
      key: `widget-${widget.id}`,
      inReport: isWidgetInReport(widget),
      widget,
      dashboard,
      onCheck: _onCheck,
      onUncheck: _onUncheck,
      checked: checked.some((wi) => isEqual(wi, widgetId(widget, dashboard)))
    }
  )) : [];
  const isEmpty = dashboard.widgets.length === 0;
  const hasNoEligibleWidgets = dashboard.widgets.every((widget) => widget.eligible === false);
  const matchingEntries = intersectionWith(dashboardWidgetRefs, checked, compareWidgetRefs);
  const isChecked = addableWidgetRefs.length > 0 && matchingEntries.length === addableWidgetRefs.length;
  const isIndetermined = matchingEntries.length > 0 && matchingEntries.length < addableWidgetRefs.length;
  const title = isEmpty ? t("dashboard_row.no_widgets") : (
    // eslint-disable-next-line no-nested-ternary
    hasNoEligibleWidgets ? t("dashboard_row.no_eligible_widgets") : expanded ? t("dashboard_row.collapse_all", { title: dashboard.title }) : t("dashboard_row.expand_all", { title: dashboard.title })
  );
  const disabled = isEmpty || hasNoEligibleWidgets || addableWidgetRefs.length === 0;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, { $disabled: disabled, title }, /* @__PURE__ */ React.createElement(SmallColumn, { $displayCursor: false }, /* @__PURE__ */ React.createElement(
    StyledCheckbox,
    {
      indeterminate: !isChecked && isIndetermined,
      disabled,
      onChange: _onChangeDashboard,
      title: isChecked ? t("dashboard_row.deselect_all_widgets", { title: dashboard.title }) : t("dashboard_row.select_all_widgets", { title: dashboard.title }),
      checked: isChecked
    }
  )), /* @__PURE__ */ React.createElement(SmallColumn, { onClick: toggleExpanded }, /* @__PURE__ */ React.createElement(ExpandButton, { expanded, title: t("dashboard_row.expand_dashboard") })), /* @__PURE__ */ React.createElement(Column, { onClick: toggleExpanded }, /* @__PURE__ */ React.createElement(DashboardIcon, null), " ", dashboard.title), /* @__PURE__ */ React.createElement(Column, { onClick: toggleExpanded }, dashboard.owner), /* @__PURE__ */ React.createElement(Column, { onClick: toggleExpanded }, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: dashboard.last_updated_at })), /* @__PURE__ */ React.createElement(Column, null, /* @__PURE__ */ React.createElement(Link, { to: `${Routes.dashboard_show(dashboard.id)}`, target: "_blank" }, /* @__PURE__ */ React.createElement(IconButton, { name: "link", title: t("dashboard_row.open_dashboard") })))), widgetRows);
};
export default DashboardRow;
